/* eslint-disable no-undef */
import { useKeycloak } from "@react-keycloak/web";
import Button from "components/Forms/CustomButtons/Button.js";
import GridContainer from "components/UI/Grid/GridContainer";
import LastAnalysis from "components/LastAnalysis";
import { Logger } from "helpers/LogHelper";
import { useSubscription } from "hooks/useSubscription";
import { format } from 'date-fns';
import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import LoadingBar from "react-top-loading-bar";
import api from "services/apiInputService";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";

import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { WorkItemResultEnum, WorkItemStatusEnum } from "utils/@types";
import {
  checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars,
  compareStringsIgnoreCaseAndRemoveSpecialChars,
} from "utils/functions";

const useStyles = makeStyles(styles);

export default function DashboardWorker() {
  const [criticProcess, setCriticProcess] = React.useState([]);
  const [approvedProcess, setApprovedProcess] = React.useState([]);
  const [processLoading, setProcessLoading] = React.useState(true);

  const ref = useRef(null);
  const classes = useStyles();
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();

  const token = keycloak.token;

  const { subscription } = useSubscription();

  React.useEffect(() => {
    if (subscription) {
      initDashboard();
    }
  }, [subscription]);

  const initDashboard = () => {
    ref.current.continuousStart();
    loadData();
  };

  const loadData = async () => {
    return await Promise.all([
      api.get(`/workItem/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`,
          subscriptionId: subscription,
        },
      }),
    ])
      .then((results) => {
        const [wItems] = results.map((r) => r.data);

        loadLastestProcesses(wItems, 6);
        setCardLoading(false);

        ref?.current?.complete();
      })
      .catch((error) => {
        ref?.current?.complete();
        Logger("Problema ao tentar carregar informações da Dashboard");
        // if (error?.response?.status == 401) {
        //   keycloak.logout({ redirectUri: `${window.location.origin}/auth/login` });
        // }
      });
  };

  const getLinkByProcessType = (processType) => {
    switch (processType.toUpperCase()) {
      case "WORKER":
        return "/app/workers/profile?type=worker&id=";
      case "SOW":
        return "/app/sows/profile?id=";
    }
  };

  const drawProcessEntry = (processInfo) => {
    return [
      processInfo.type,
      `${processInfo.workerID}`,
      format(new Date(processInfo.createdAt), "dd/MM/yyyy HH:mm"),
      <Button
        round
        color="info"
        className={classes.actionButton + " " + classes.actionButtonRound}
        onClick={() =>
          navigate(
            getLinkByProcessType(processInfo.typeCommand) +
              processInfo.workerID,
          )
        }
        key={new Date().getTime()}
      >
        <ArrowForwardIosIcon className={classes.icon} />
      </Button>,
    ];
  };

  const GetDocumentTypeByCode = (code) => {
    switch (code) {
      case "PSU001":
        return "TGSS";

      case "PSU002":
        return "AEAT";

      case "PWU001":
        return "RNT/RECIBO/A1";

      case "PWU002":
        return "IDC/IDC RETA";

      case "PRL006":
        return "PRL";
      default:
        return code;
    }
  };

  const loadLastestProcesses = (wItems, quantity = 3) => {
    const wItemsHomologated = wItems
      .filter(
        (wi) =>
          checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars(wi.status, [
            WorkItemResultEnum.HOMOLOGADO,
            WorkItemResultEnum.Approved,
          ]) ||
          compareStringsIgnoreCaseAndRemoveSpecialChars(
            wi.status,
            WorkItemStatusEnum.HOMOLOGADO_MANUALMENTE,
          ),
      )
      .slice(-quantity);
    const wItemsNotHomologated = wItems
      .filter(
        (wi) =>
          checkIfStringInArrayIgnoreCaseAndRemoveSpecialChars(wi.status, [
            WorkItemResultEnum.NAO_HOMOLOGADO,
            WorkItemResultEnum.Not_Approved,
          ]) &&
          compareStringsIgnoreCaseAndRemoveSpecialChars(
            wi.status,
            WorkItemStatusEnum.FINISH,
          ),
      )
      .slice(-quantity);

    const wItemsHomologatedFormatted = wItemsHomologated.map((procs) => ({
      type: procs.type
        ? GetDocumentTypeByCode(procs.type.toUpperCase())
        : procs.type.toUpperCase(),
      workerID: procs.requestParam,
      createdAt: procs.createdAt,
      typeCommand: procs.profile,
    }));

    const wItemsNotHomologatedFormatted = wItemsNotHomologated.map((procs) => ({
      type: procs.type
        ? GetDocumentTypeByCode(procs.type.toUpperCase())
        : procs.type.toUpperCase(),
      workerID: procs.requestParam,
      createdAt: procs.createdAt,
      typeCommand: procs.profile,
    }));

    setApprovedProcess([
      ...wItemsHomologatedFormatted.map((wItem) => drawProcessEntry(wItem)),
    ]);
    setCriticProcess([
      ...wItemsNotHomologatedFormatted.map((wItem) => drawProcessEntry(wItem)),
    ]);
    setProcessLoading(false);
  };

  return (
    <div>
      <LoadingBar color="#ff9800" ref={ref} />
      <GridContainer>
        <LastAnalysis
          homologated
          processes={[...approvedProcess]}
          processLoading={processLoading}
        />
        <LastAnalysis
          processes={[...criticProcess]}
          processLoading={processLoading}
        />
      </GridContainer>
    </div>
  );
}
